// src/pages/OrdersProfitPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";
import DateRange from "../components/DateRange";

/** ---------- Types (ตาม /orders/summary) ---------- */
type Row = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  packageFee: number;
  shippingFee: number;
  discountAmount: number | null;
  afterDiscount: number | null;
  productCode: string | null;
  productName: string | null;
  qty: number | null;
  unitPrice: number | null;
  tiktokNetPayout: number | null;
  unitCost: number | null;
  profit: number | null;
  orderCreated: string;
};

type OrderItem = {
  productCode: string;
  productName: string;
  qty: number;
  unitPrice: number;
  actualUnitPrice: number;
  tiktokNetPayout: number | null;
  unitCost: number;
  lineTotal: number;
  lineProfit: number;
};

type Order = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  packageFee: number;
  shippingFee: number;
  discountAmount: number;
  afterDiscount: number;
  createdAt: string;
  items: OrderItem[];
  billProfit: number;
};

// [ADD] Payment types & list (วางต่อท้าย type Order)
type PaymentMethod = "เงินสด" | "สแกน" | "Alipay" | "WeChat" | "บัตรเดบิต";
const PAYMENT_METHODS: PaymentMethod[] = ["เงินสด", "สแกน", "Alipay", "WeChat", "บัตรเดบิต"];
const DEFAULT_VISIBLE_ORDERS = 100;
const PAGED_VISIBLE_ORDERS = 200;

function formatSaleChannel(channel: string | null | undefined): string {
  if (!channel) return "-";
  return channel;
}

/** ---------- Helpers ---------- */
const thb = (n: number) => n.toLocaleString("th-TH", { style: "currency", currency: "THB" });

// dd/mm/yyyy (พ.ศ.) หรือ yyyy-mm-dd (พ.ศ.) + time → Date(ค.ศ.)
// PMN-CRITICAL(/OrdersProfitPage): Date filters compare AD Date objects, but recDateBE is stored as BE year; keep the BE->AD conversion here.
function beDMYToDate(dmy: string, time?: string): Date | null {
  const hhmmss = time && /^\d{2}:\d{2}(:\d{2})?$/.test(time) ? time : "00:00:00";

  const slash = dmy.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const dd = Number(slash[1]);
    const mm = Number(slash[2]);
    const yyyyBE = Number(slash[3]);
    const yyyyAD = yyyyBE - 543;
    return new Date(`${yyyyAD}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}T${hhmmss}`);
  }

  const iso = dmy.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const yyyyBE = Number(iso[1]);
    const mm = Number(iso[2]);
    const dd = Number(iso[3]);
    const yyyyAD = yyyyBE - 543;
    return new Date(`${yyyyAD}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}T${hhmmss}`);
  }

  return null;
}

function toYMDLocal(dt: Date): string {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`; // ใช้เวลาเครื่อง ไม่ใช่ UTC
}

function formatThaiLong(dt: Date): string {
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "long",
    timeStyle: "short",
    hour12: false,
  }).format(dt) + " น.";
}

function formatThaiShort(dt: Date): string {
  const d = new Intl.DateTimeFormat("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dt);
  return d + " น.";
}

// คืน Date ของบิล: ใช้ recDateBE ก่อน แล้วค่อย fallback ไป createdAt
function resolveOrderDate(o: Order): Date | null {
  if (o.recDateBE) {
    const d = beDMYToDate(o.recDateBE, o.recTime ?? undefined);
    if (d) return d;
  }
  if (o.createdAt) {
    const d = new Date(o.createdAt);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatOrderDateTime(opts: {
  createdAt?: string;
  recDateBE?: string | null;
  recTime?: string | null;
  variant?: "long" | "short";
}): string {
  const { createdAt, recDateBE, recTime, variant = "short" } = opts;
  let dt: Date | null = null;

  if (recDateBE) dt = beDMYToDate(recDateBE, recTime ?? undefined);
  if (!dt && createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) dt = d;
  }
  if (!dt && recDateBE) {
    const tryISO = new Date(recDateBE);
    if (!Number.isNaN(tryISO.getTime())) dt = tryISO;
  }
  if (!dt) return "-";
  return variant === "long" ? formatThaiLong(dt) : formatThaiShort(dt);
}

/** แปลง rows จาก /orders/summary → ออบเจ็กต์บิล พร้อมคำนวณกำไรต่อบิล */
// PMN-CRITICAL(/OrdersProfitPage): This recomputes bill profit from summary rows; TikTok payout, discount, package fee, and shipping fee rules live here.
function rowsToOrders(rows: Row[]): Order[] {
  const map = new Map<string, (Order & { _accProfit: number })>();

  for (const r of rows) {
    if (!map.has(r.billNo)) {
      const discount = r.discountAmount ?? 0;
      const after =
        r.afterDiscount != null ? r.afterDiscount : Math.max(0, (r.orderTotal ?? 0) - discount);

      map.set(r.billNo, {
        billNo: r.billNo,
        paymentMethod: r.paymentMethod ?? null,
        saleChannel: r.saleChannel ?? null,
        recDateBE: r.recDateBE ?? null,
        recTime: r.recTime ?? null,
        totalItems: r.totalItems ?? 0,
        orderTotal: r.orderTotal ?? 0,
        packageFee: r.packageFee ?? 0,
        shippingFee: r.shippingFee ?? 0,
        discountAmount: discount,
        afterDiscount: after,
        createdAt: r.orderCreated,
        items: [],
        billProfit: 0,
        _accProfit: 0,
      });
    }

    // แถวสินค้าที่มีจริงเท่านั้น
    if (r.productCode || r.productName) {
      const qty   = Number(r.qty ?? 0);
      const price = Number(r.unitPrice ?? 0);
      const tiktokNetPayout = r.tiktokNetPayout == null ? null : Number(r.tiktokNetPayout);
      const actualUnitPrice =
        tiktokNetPayout != null && Number.isFinite(tiktokNetPayout)
          ? tiktokNetPayout
          : price;
      const profitUnitPrice =
        tiktokNetPayout != null && Number.isFinite(tiktokNetPayout) && tiktokNetPayout > 0
          ? tiktokNetPayout
          : price;
      const cost  = Number(r.unitCost ?? 0);
      const lineProfit = qty * (profitUnitPrice - cost);

      const o = map.get(r.billNo)!;
      o.items.push({
        productCode: r.productCode ?? "-",
        productName: r.productName ?? "-",
        qty,
        unitPrice: price,
        actualUnitPrice,
        tiktokNetPayout,
        unitCost: cost,
        lineTotal: qty * price,
        lineProfit,
      });
      o._accProfit += lineProfit;
    }
  }

  const orders: Order[] = Array.from(map.values()).map(({ _accProfit, ...o }) => ({
    ...o,
    billProfit:
      (_accProfit ?? 0) -
      (o.discountAmount ?? 0) -
      (o.saleChannel === "TIKTOK_SHOP" ? (o.packageFee ?? 0) + (o.shippingFee ?? 0) : 0),
  }));

  orders.sort((a, b) => {
    const an = parseInt(a.billNo, 10);
    const bn = parseInt(b.billNo, 10);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an;

    const ad = new Date(a.createdAt).getTime();
    const bd = new Date(b.createdAt).getTime();
    if (Number.isFinite(ad) && Number.isFinite(bd)) return bd - ad;

    return b.billNo.localeCompare(a.billNo, undefined, { numeric: true, sensitivity: "base" });
  });

  return orders;
}

// [ADD] API layer สำหรับอัปเดตวิธีชำระเงิน
// PMN-CRITICAL(/OrdersProfitPage): Frontend currently expects { ok: true }, but the shared backend payment route may return only { message }.
async function patchPaymentMethod(
  billNo: string,
  paymentMethod: PaymentMethod | null
): Promise<void> {
  const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(billNo)}/payment-method`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paymentMethod }),
  });
  const j = await resp.json().catch(() => ({}));
  if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);
}

/** ============================================================= */

export default function OrdersProfitPage(): JSX.Element {
  const [raw, setRaw] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  const [query, setQuery] = useState<string>("");
  const [paymentFilter, setPaymentFilter] = useState<string>("");
  const [saleChannelFilter, setSaleChannelFilter] = useState<string>("");

  // (A) เก็บช่วงวันเป็นสตริงสำหรับฟิลเตอร์ (YYYY-MM-DD)
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // (B) เก็บช่วงวันเป็น Date object สำหรับ UI DateRange (dd/MM/yyyy)
  const [dateFromObj, setDateFromObj] = useState<Date | null>(null);
  const [dateToObj, setDateToObj] = useState<Date | null>(null);

  const [selected, setSelected] = useState<Order | null>(null);

  const [showPayModal, setShowPayModal] = useState<boolean>(false);
  const [draftPay, setDraftPay] = useState<PaymentMethod | null>(null);
  const [savingPay, setSavingPay] = useState<boolean>(false);
  const [saveErr, setSaveErr] = useState<string>("");
  const [showAllOrders, setShowAllOrders] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // ซิงก์ UI → ฟิลเตอร์สตริง
  useEffect(() => {
    setDateFrom(dateFromObj ? toYMDLocal(dateFromObj) : "");
  }, [dateFromObj]);
  
  useEffect(() => {
    setDateTo(dateToObj ? toYMDLocal(dateToObj) : "");
  }, [dateToObj]);

  const orders = useMemo(() => rowsToOrders(raw), [raw]);
  const paymentOptions = useMemo(() => {
    return Array.from(
      new Set(
        orders
          .map((order) => order.paymentMethod)
          .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      )
    );
  }, [orders]);

  const saleChannelOptions = useMemo(() => {
    return Array.from(
      new Set(
        orders
          .map((order) => order.saleChannel)
          .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      )
    );
  }, [orders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (q && !o.billNo.toLowerCase().includes(q)) return false;
      if (paymentFilter && o.paymentMethod !== paymentFilter) return false;
      if (saleChannelFilter && o.saleChannel !== saleChannelFilter) return false;

      if (!dateFrom && !dateTo) return true;

      const d = resolveOrderDate(o);
      if (!d) return false;

      const ymd = toYMDLocal(d);
      if (dateFrom && ymd < dateFrom) return false;
      if (dateTo && ymd > dateTo) return false;

      return true;
    });
  }, [orders, query, paymentFilter, saleChannelFilter, dateFrom, dateTo]);

  const visibleOrders = useMemo(() => {
    if (showAllOrders) {
      const start = (currentPage - 1) * PAGED_VISIBLE_ORDERS;
      return filtered.slice(start, start + PAGED_VISIBLE_ORDERS);
    }
    return filtered.slice(0, DEFAULT_VISIBLE_ORDERS);
  }, [filtered, showAllOrders, currentPage]);

  const totalPages = useMemo(() => {
    if (!showAllOrders) return 1;
    return Math.max(1, Math.ceil(filtered.length / PAGED_VISIBLE_ORDERS));
  }, [filtered.length, showAllOrders]);

  useEffect(() => {
    setShowAllOrders(false);
    setCurrentPage(1);
  }, [query, paymentFilter, saleChannelFilter, dateFrom, dateTo, raw]);

  useEffect(() => {
    if (!showAllOrders) {
      setCurrentPage(1);
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [showAllOrders, currentPage, totalPages]);

  const load = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
  
    if (!silent) {
      setLoading(true);
      setErr("");
    }
  
    try {
      const resp = await fetch(apiUrl("/orders-summary"));
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
      const items: Row[] = Array.isArray(j?.items) ? j.items : [];
      setRaw(items);
    } catch (e: any) {
      if (!silent) {
        setErr(e?.message || "โหลดข้อมูลบิลไม่สำเร็จ");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const printReceipt = () => {
    if (!selected) return;
    const doc = window.open("", "_blank", "width=650,height=800");
    if (!doc) return;
    const html = renderReceiptHtml(selected);
    doc.document.open();
    doc.document.write(html);
    doc.document.close();
    setTimeout(() => doc.print(), 400);
  };
  // [ADD] เปิดหน้าต่างแก้ไขวิธีชำระ
  const openPayEditor = (o: Order): void => {
    setSelected(o);
    setDraftPay((o.paymentMethod as PaymentMethod | null) ?? null);
    setSaveErr("");
    setShowPayModal(true);  // << เดิมเป็น setShowPayEdit(true)
  };

  // [ADD] บันทึกวิธีชำระ (optimistic update + rollback ถ้า error)
  // PMN-CRITICAL(/OrdersProfitPage): Payment edit is the only write path on this page; rollback raw/selected if the PATCH contract fails.
  const savePayment = async (next?: PaymentMethod | null): Promise<void> => {
    if (!selected) return;
    const billNo = selected.billNo;
    const original = selected.paymentMethod;
    const newValue = next !== undefined ? next : draftPay;
  
    if (original === newValue) {
      setShowPayModal(false);
      return;
    }
  
    setSavingPay(true);
    setSaveErr("");
  
    // optimistic update
    setSelected({ ...selected, paymentMethod: newValue ?? null });
    setRaw((prev) => prev.map((r) => (r.billNo === billNo ? { ...r, paymentMethod: newValue ?? null } : r)));
  
    try {
      await patchPaymentMethod(billNo, newValue ?? null);
      await load({ silent: true });    // <— รีเฟรชข้อมูลใหม่ทันทีแบบเงียบ
      setShowPayModal(false);
     
    } catch (e: unknown) {
      // rollback
      setSelected((prev) => (prev ? { ...prev, paymentMethod: original } : prev));
      setRaw((prev) => prev.map((r) => (r.billNo === billNo ? { ...r, paymentMethod: original } : r)));
      const msg = e instanceof Error ? e.message : "บันทึกวิธีชำระเงินไม่สำเร็จ";
      setSaveErr(msg);
    } finally {
     
      setSavingPay(false);
    }
  };


  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">รายการบิล (พร้อมกำไรต่อบิล)</h4>

                {/* แถบเครื่องมือ */}
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                  <div className="input-group" style={{ maxWidth: 360 }}>
                    <span className="input-group-text">ค้นหา</span>
                    <input
                      className="form-control"
                      placeholder="เลขบิล เช่น 13"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <select
                      className="form-select"
                      style={{ minWidth: 160 }}
                      value={paymentFilter}
                      onChange={(e) => setPaymentFilter(e.target.value)}
                    >
                      <option value="">ทุกวิธีชำระ</option>
                      {paymentOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>

                    <select
                      className="form-select"
                      style={{ minWidth: 160 }}
                      value={saleChannelFilter}
                      onChange={(e) => setSaleChannelFilter(e.target.value)}
                    >
                      <option value="">ทุกสถานที่ขาย</option>
                      {saleChannelOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ ใช้ DateRange (แสดง dd/MM/yyyy) */}
                  <DateRange
                    from={dateFromObj}
                    to={dateToObj}
                    onChangeFrom={setDateFromObj}
                    onChangeTo={setDateToObj}
                  />

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => void load()}      // <-- เปลี่ยนจาก onClick={load}
                      disabled={loading}
                      title="รีเฟรชข้อมูล"
                    >
                      {loading ? "กำลังโหลด…" : "รีเฟรช"}
                    </button>
                    <a href="/" className="btn btn-link text-decoration-none">
                      ← กลับหน้าเมนู
                    </a>
                  </div>
                </div>

                {err && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    {err}
                  </div>
                )}

                {/* ตารางบิลสรุป */}
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 120 }}>เลขบิล</th>
                        <th style={{ width: 180 }}>วันที่</th>
                        <th style={{ width: 120 }}>วิธีชำระ</th>
                        <th style={{ width: 120 }}>สถานที่ขาย</th>
                        <th className="text-end" style={{ width: 120 }}>จำนวนสินค้า</th>
                        <th className="text-end" style={{ width: 160 }}>ยอดสุทธิ</th>
                        <th className="text-end" style={{ width: 160 }}>กำไรต่อบิล</th>
                        <th style={{ width: 140 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOrders.map((o) => (
                        <tr key={o.billNo}>
                          <td className="fw-semibold text-center">#{o.billNo}</td>
                          <td>
                            {formatOrderDateTime({
                              createdAt: o.createdAt,
                              recDateBE: o.recDateBE,
                              recTime: o.recTime,
                              variant: "short",
                            })}
                          </td>
                          <td className="text-center">{o.paymentMethod ?? "-"}</td>
                          <td className="text-center">{formatSaleChannel(o.saleChannel)}</td>
                          <td className="text-center">{o.totalItems}</td>
                          <td className="text-end">{thb(o.afterDiscount)}</td>
                          <td className="text-end">
                            <span className={o.billProfit >= 0 ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                              {thb(o.billProfit)}
                            </span>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => setSelected(o)}>
                              ดูบิล
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!loading && visibleOrders.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            ไม่พบบิล
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && filtered.length > DEFAULT_VISIBLE_ORDERS && (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mt-3">
                    <div className="small text-muted">
                      {showAllOrders
                        ? `หน้า ${currentPage.toLocaleString()} / ${totalPages.toLocaleString()} • กำลังแสดง ${visibleOrders.length.toLocaleString()} จาก ${filtered.length.toLocaleString()} บิล`
                        : `กำลังแสดง ${visibleOrders.length.toLocaleString()} จาก ${filtered.length.toLocaleString()} บิล`}
                    </div>
                    <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                      {showAllOrders && totalPages > 1 && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage <= 1}
                          >
                            ก่อนหน้า
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={currentPage >= totalPages}
                          >
                            ถัดไป
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setShowAllOrders((prev) => !prev);
                          setCurrentPage(1);
                        }}
                      >
                        {showAllOrders ? "แสดงเฉพาะ 100 บิลแรก" : "ดูทั้งหมด"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * โทนการ์ดยึดสไตล์เดียวกับหน้าเพิ่มสินค้า
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Modal ใบเสร็จ + กำไรบิล ---------- */}
      {selected && (
        <div
          className="modal fade show"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">ใบเสร็จ #{selected.billNo}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelected(null)} />
              </div>

              <div className="modal-body">
                <div className="d-flex justify-content-between flex-wrap">
                  <div>
                    <div className="fw-bold">บิลเลขที่: #{selected.billNo}</div>
                    <div className="text-muted">
                      วันที่: {formatOrderDateTime({
                        createdAt: selected.createdAt,
                        recDateBE: selected.recDateBE,
                        recTime: selected.recTime,
                        variant: "long",
                      })}
                    </div>
                    <div className="text-muted">วิธีชำระ: {selected.paymentMethod ?? "-"}
                      <button
                        className="btn btn-outline-secondary btn-sm ms-2"
                        onClick={() => openPayEditor(selected)}
                        title="เปลี่ยนวิธีชำระเงิน"
                      >
                        เปลี่ยนวิธีชำระ
                      </button>
                    </div>
                    <div className="text-muted">สถานที่ขาย: {formatSaleChannel(selected.saleChannel)}</div>
                   
                 
                  </div>
                  <div className="text-end">
                    <div>ยอดก่อนส่วนลด: {thb(selected.orderTotal)}</div>
                    <div>ส่วนลด: {thb(selected.discountAmount)}</div>
                    {selected.saleChannel === "TIKTOK_SHOP" && (
                      <>
                        <div>Package Fee: {thb(selected.packageFee)}</div>
                        <div>Shipping Fee: {thb(selected.shippingFee)}</div>
                      </>
                    )}
                    <div className="fs-5 fw-bold">ยอดสุทธิ: {thb(selected.afterDiscount)}</div>
                    <div className="mt-1">
                      กำไรต่อบิล:{" "}
                      <span className={selected.billProfit >= 0 ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                        {thb(selected.billProfit)}
                      </span>
                    </div>
                  </div>
                </div>

                <hr />

                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 110 }}>รหัส</th>
                        <th>สินค้า</th>
                        <th className="text-end" style={{ width: 80 }}>จำนวน</th>
                        <th className="text-end" style={{ width: 120 }}>ขาย/หน่วย</th>
                        <th className="text-end" style={{ width: 140 }}>ขาย/หน่วยจริง</th>
                        <th className="text-end" style={{ width: 120 }}>ต้นทุน/หน่วย</th>
                        <th className="text-end" style={{ width: 120 }}>กำไร/แถว</th>
                        <th className="text-end" style={{ width: 120 }}>รวม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items.map((it, idx) => (
                        <tr key={idx}>
                          <td className="text-nowrap text-center">{it.productCode}</td>
                          <td className="text-start">{it.productName}</td>
                          <td className="text-end">{it.qty}</td>
                          <td className="text-end">{thb(it.unitPrice)}</td>
                          <td className="text-end">{thb(it.actualUnitPrice)}</td>
                          <td className="text-end">{thb(it.unitCost)}</td>
                          <td className="text-end">
                            <span className={it.lineProfit >= 0 ? "text-success" : "text-danger"}>
                              {thb(it.lineProfit)}
                            </span>
                          </td>
                          <td className="text-end">{thb(it.lineTotal)}</td>
                        </tr>
                      ))}
                      {selected.items.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            ไม่มีรายการสินค้า
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  
                </div>
              </div>
              {selected && showPayModal && (
                <div
                  className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                  onClick={(e) => { if (e.target === e.currentTarget) setShowPayModal(false); }}
                >
                  <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 720, width: "92%" }}>
                    <div className="card-body p-4">
                      <h5 className="mb-3 text-center">เลือกวิธีชำระเงินสำหรับบิล #{selected.billNo}</h5>

                      <div className="row row-cols-2 g-3">
                        {PAYMENT_METHODS.map((opt) => (
                          <div className="col" key={opt}>
                            <button
                              type="button"
                              className={`card w-100 h-100 border-2 rounded-4 text-decoration-none ${
                                draftPay === opt ? "border-primary bg-secondary text-black" : "border-light"
                              }`}
                              onClick={() => setDraftPay(opt)}   // ✅ เลือกเก็บใน draftPay ยังไม่เซฟ
                              title={`เลือก ${opt}`}
                            >
                              <div
                                className="card-body d-flex align-items-center justify-content-center"
                                style={{ minHeight: 100, cursor: "pointer" }}
                              >
                                <div className="text-center">
                                  <div className="fw-bold">{opt}</div>
                                  {draftPay === opt && <div className="small text-primary mt-1">✓ เลือกแล้ว</div>}
                                </div>
                              </div>
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <button
                          type="button"
                          className={`btn ${draftPay === null ? "btn-warning" : "btn-outline-warning"}`}
                          onClick={() => setDraftPay(null)}      // ✅ ล้างค่า (ยังไม่เซฟ)
                          disabled={savingPay}
                        >
                          ล้างค่า
                        </button>

                        <div className="d-flex gap-2">
                          {saveErr && <span className="text-danger small align-self-center">{saveErr}</span>}
                          
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              // ยกเลิก: กลับค่าเดิมแล้วปิด
                              setDraftPay((selected?.paymentMethod as PaymentMethod | null) ?? null);
                              setShowPayModal(false);
                              setSaveErr("");
                            }}
                          >
                            ปิด
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => void savePayment()}  // ✅ เซฟค่าจาก draftPay
                            disabled={savingPay || selected.paymentMethod === draftPay}
                            title={selected.paymentMethod === draftPay ? "ไม่มีการเปลี่ยนแปลง" : "บันทึกวิธีชำระเงิน"}
                          >
                            {savingPay ? "กำลังบันทึก…" : "บันทึก"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="modal-footer d-flex justify-content-between">
                <div className="text-muted">สร้างเมื่อ: {new Date(selected.createdAt).toLocaleString("th-TH")}</div>
                <div className="d-flex gap-2">
                  
                  <button className="btn btn-outline-secondary" onClick={() => setSelected(null)}>ปิด</button>
                  <button className="btn btn-primary" onClick={printReceipt}>พิมพ์ใบเสร็จ</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** ---------- HTML สำหรับพิมพ์ (เพิ่มกำไรบิล) ---------- */
function renderReceiptHtml(o: Order): string {
  const rows = o.items.map((it) => `
    <tr>
      <td>${escapeHtml(it.productCode)}</td>
      <td>${escapeHtml(it.productName)}</td>
      <td class="t-right">${it.qty}</td>
      <td class="t-right">${thb(it.unitPrice)}</td>
      <td class="t-right">${thb(it.unitCost)}</td>
      <td class="t-right">${thb(it.lineProfit)}</td>
      <td class="t-right">${thb(it.lineTotal)}</td>
    </tr>
  `).join("");

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt #${o.billNo}</title>
<style>
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, "TH Sarabun New", sans-serif; padding: 16px; }
  .header { display:flex; justify-content:space-between; gap:12px; }
  .muted { color:#666; font-size:12px; }
  table { width:100%; border-collapse: collapse; margin-top: 12px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 14px; }
  th { background:#f7f7f7; text-align:left; }
  .t-right { text-align:right; }
  .summary { margin-top: 14px; }
  .summary .row { display:flex; justify-content:flex-end; gap: 24px; }
  .summary .label { color:#444; }
  .summary .value { min-width: 160px; text-align: right; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div><strong>ใบเสร็จรับเงิน</strong></div>
      <div class="muted">บิลเลขที่: #${o.billNo}</div>
      <div class="muted">วันที่: ${o.recDateBE ?? "-"} ${o.recTime ?? ""}</div>
      <div class="muted">วิธีชำระ: ${escapeHtml(o.paymentMethod ?? "-")}</div>
    </div>
    <div style="text-align:right">
      <div class="muted">สร้างเมื่อ</div>
      <div>${new Date(o.createdAt).toLocaleString("th-TH")}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:100px">รหัส</th>
        <th>รายการสินค้า</th>
        <th class="t-right" style="width:70px">จำนวน</th>
        <th class="t-right" style="width:110px">ขาย/หน่วย</th>
        <th class="t-right" style="width:110px">ต้นทุน/หน่วย</th>
        <th class="t-right" style="width:110px">กำไร/แถว</th>
        <th class="t-right" style="width:120px">รวม</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="7" class="muted" style="text-align:center">ไม่มีรายการสินค้า</td></tr>'}
    </tbody>
  </table>

  <div class="summary">
    <div class="row"><div class="label">ยอดก่อนส่วนลด</div><div class="value">${thb(o.orderTotal)}</div></div>
    <div class="row"><div class="label">ส่วนลด</div><div class="value">${thb(o.discountAmount)}</div></div>
    <div class="row"><div class="label"><strong>ยอดสุทธิ</strong></div><div class="value"><strong>${thb(o.afterDiscount)}</strong></div></div>
    <div class="row"><div class="label">กำไรต่อบิล</div><div class="value"><strong>${thb(o.billProfit)}</strong></div></div>
  </div>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
